/**
 * Vendor Performance Report (20.3) — table, drill-down, charts.
 */

import { useCallback, useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { fetchVendorPerformanceReport, exportVendorPerformanceCSV, exportVendorPerformancePDF } from '../../api/reports';
import { LoadingSpinner, EmptyState } from '../common';
import ReportShell from './ReportShell';

function fmtCurrency(v) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v || 0);
}

export default function VendorPerformanceReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vendorFilter, setVendorFilter] = useState('');
  const [exporting, setExporting] = useState(null);

  const params = { vendor_id: vendorFilter || undefined };

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await fetchVendorPerformanceReport(params)); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [vendorFilter]);

  useEffect(() => { load(); }, [load]);

  async function handleExport(type) {
    setExporting(type);
    try {
      if (type === 'csv') await exportVendorPerformanceCSV(params);
      else await exportVendorPerformancePDF(params);
    } catch {} finally { setExporting(null); }
  }

  const rows = data?.rows || [];

  // Aggregate by vendor for chart
  const vendorAgg = {};
  rows.forEach((r) => {
    if (!vendorAgg[r.vendor_name]) vendorAgg[r.vendor_name] = { spend: 0, leadTime: 0, onTime: 0, count: 0 };
    const v = vendorAgg[r.vendor_name];
    v.spend += parseFloat(r.total_spend) || 0;
    v.leadTime += parseFloat(r.avg_lead_time_days) || 0;
    v.onTime += parseFloat(r.on_time_pct) || 0;
    v.count++;
  });
  const chartData = Object.entries(vendorAgg).map(([name, v]) => ({
    name: name.length > 15 ? name.slice(0, 15) + '...' : name,
    spend: v.spend,
    avgLeadTime: v.count ? (v.leadTime / v.count) : 0,
    onTimePct: v.count ? (v.onTime / v.count) : 0,
  })).slice(0, 15);

  return (
    <ReportShell
      title="Vendor Performance"
      exporting={exporting}
      onExportCSV={() => handleExport('csv')}
      onExportPDF={() => handleExport('pdf')}
      filters={
        <>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Vendor ID</label>
            <input
              type="text"
              className="border border-gray-300 rounded-md px-2 py-1 text-sm w-48"
              placeholder="Filter by vendor..."
              value={vendorFilter}
              onChange={(e) => setVendorFilter(e.target.value)}
            />
          </div>
          <button onClick={load} className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 self-end">Run</button>
        </>
      }
    >
      {loading ? <div className="flex justify-center py-10"><LoadingSpinner /></div> : rows.length === 0 ? (
        <EmptyState title="No vendor data" description="Vendor performance metrics will appear after quotes and deliveries." />
      ) : (
        <>
          <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                  <th className="px-3 py-2">Vendor</th>
                  <th className="px-3 py-2">Part #</th>
                  <th className="px-3 py-2">Vendor PN</th>
                  <th className="px-3 py-2 text-right">Avg Lead Time</th>
                  <th className="px-3 py-2 text-right">On-Time %</th>
                  <th className="px-3 py-2 text-right">Quotes</th>
                  <th className="px-3 py-2 text-right">Total Spend</th>
                  <th className="px-3 py-2 text-right">PO Lines</th>
                  <th className="px-3 py-2">Last Quote</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.vendor_part_id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-foreground">{r.vendor_name}</td>
                    <td className="px-3 py-2 text-gray-600">{r.part_number}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.vendor_part_number}</td>
                    <td className="px-3 py-2 text-right">{r.avg_lead_time_days ? `${r.avg_lead_time_days}d` : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      {r.on_time_pct != null ? (
                        <span className={parseFloat(r.on_time_pct) >= 90 ? 'text-green-600' : parseFloat(r.on_time_pct) >= 70 ? 'text-yellow-600' : 'text-red-600'}>
                          {r.on_time_pct}%
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">{r.total_quotes}</td>
                    <td className="px-3 py-2 text-right">{fmtCurrency(r.total_spend)}</td>
                    <td className="px-3 py-2 text-right">{r.total_po_lines}</td>
                    <td className="px-3 py-2 text-gray-400 text-xs">{r.last_quote_date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {chartData.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">Spend by Vendor</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="spend" fill="#6366f1" name="Total Spend ($)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </ReportShell>
  );
}
